import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { usePathname, useRouter } from '~i18n/navigation';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import { CUSTOM_VALUE_QUERY_PARAM_PREFIX } from '../model/constants';
import {
	isValueMultiSelect,
	isValueSimple,
	isValueSliders,
} from '../model/guards';
import type {
	BotWizardField,
	BotWizardFieldNames,
	BotWizardFieldSliders,
	BotWizardValues,
	BotWizardValueSliders,
} from '../model/types';
import { convertSliderValueToQuery } from '../model/utils';

export const useUpdateQuery = ({
	values,
	fields,
	customValues,
}: {
	values: BotWizardValues;
	fields: Record<BotWizardFieldNames, BotWizardField>;
	customValues: { [key in BotWizardFieldNames]?: string };
}) => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	useEffect(() => {
		const simpleValues = Object.fromEntries(
			Object.entries(values)
				.filter(
					([key]) =>
						key === 'path' ||
						key === 'step' ||
						isValueSimple(
							fields[key as BotWizardFieldNames],
							values,
							key as BotWizardFieldNames
						)
				)
				.map(([key, value]) => [
					fields[key as BotWizardFieldNames]?.queryKey ?? key,
					value,
				])
		) as Record<string, string | undefined>;
		const sliderValues = Object.entries(values)
			.filter(
				([key]) =>
					!simpleValues[key] &&
					isValueSliders(
						fields[key as BotWizardFieldNames],
						values,
						key as BotWizardFieldNames
					)
			)
			.map(([key, value]) =>
				convertSliderValueToQuery(
					value as BotWizardValueSliders,
					fields[key as BotWizardFieldNames] as BotWizardFieldSliders
				)
			)
			.reduce((acc, el) => ({ ...acc, ...el }));

		const multiselectValues = Object.fromEntries(
			Object.entries(values).filter(
				([key]) =>
					!simpleValues[key] &&
					!sliderValues[key] &&
					isValueMultiSelect(
						fields[key as BotWizardFieldNames],
						values,
						key as BotWizardFieldNames
					)
			)
		) as Record<string, string[]>;

		const customSimpleValues = Object.fromEntries(
			Object.entries(customValues).map(([key, value]) => [
				CUSTOM_VALUE_QUERY_PARAM_PREFIX +
					(fields[key as BotWizardFieldNames]?.queryKey ?? key),
				value,
			])
		) as Record<string, string | undefined>;

		const newUrl = updateQueryParamsWithPathname(
			{
				path: values.path as string,
				step: values.step as string,
				...simpleValues,
				...sliderValues,
				...customSimpleValues,
				...multiselectValues,
			},
			pathname,
			searchParams
		);
		if (newUrl !== pathname + '?' + searchParams.toString()) {
			router.replace(newUrl, { scroll: false });
		}
	}, [values, customValues, fields, pathname, searchParams, router]);
};
